import { HttpClient } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { GlobalConstants } from '../global-constants';
import { AuthService } from '../services/auth.service';

@Component({
  selector: 'app-payment-return',
  templateUrl: './payment-return.component.html',
  styleUrls: ['./payment-return.component.less']
})
export class PaymentReturnComponent implements OnInit {
  status: 'processing' | 'success' | 'error' | 'pending' = 'processing';
  message = '';

  constructor(private http: HttpClient, private router: Router, private route: ActivatedRoute, private authService: AuthService) {}

  ngOnInit(): void {
    // Stripe may place params before the hash (?...#/payment-return) or inside it
    // (#/payment-return?...). Check both sources to cover either case.
    const qp = this.route.snapshot.queryParams;
    const search = new URLSearchParams(window.location.search);
    // New bookings confirm via confirmSetup(), which redirects back with
    // `setup_intent=`, not `payment_intent=` — both must be checked.
    const setupIntentParam = qp['setup_intent'] || search.get('setup_intent') || '';
    const paymentIntentParam = qp['payment_intent'] || search.get('payment_intent') || '';
    const piId = setupIntentParam || paymentIntentParam || localStorage.getItem('stripe_return_pi') || '';
    const piType: 'setup_intent' | 'payment_intent' = setupIntentParam
      ? 'setup_intent'
      : paymentIntentParam
        ? 'payment_intent'
        : (localStorage.getItem('stripe_return_type') as 'setup_intent' | 'payment_intent') || 'payment_intent';
    const redirectStatus = qp['redirect_status']
      || search.get('redirect_status')
      || localStorage.getItem('stripe_return_status')
      || '';
    const mode = localStorage.getItem('payment_return_mode') || 'new_booking';
    const tripId = localStorage.getItem('payment_return_trip_id') || '';
    const taxCalcId = localStorage.getItem('stripe_tax_calc_id') || '';
    const booking = JSON.parse(localStorage.getItem('booking') || '{}');
    const payable = parseFloat(localStorage.getItem('payment_return_payable') || '')
      || booking?.data?.[0]?.total_price
      || 0;

    // Clean up
    localStorage.removeItem('stripe_return_pi');
    localStorage.removeItem('stripe_return_type');
    localStorage.removeItem('stripe_return_status');
    localStorage.removeItem('payment_return_mode');
    localStorage.removeItem('payment_return_trip_id');
    localStorage.removeItem('payment_return_payable');
    localStorage.removeItem('payment_mode');
    localStorage.removeItem('stripe_tax_calc_id');

    // Clean Stripe params from the browser URL
    if (window.location.search) {
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    if (!piId) {
      this.router.navigate(['/my-bookings']);
      return;
    }

    if (redirectStatus === 'succeeded') {
      if (mode === 'existing_trip') {
        this.confirmPayment(piId, tripId, taxCalcId);
      } else {
        this.confirmBooking(piId, piType, payable, taxCalcId);
      }
    } else if (redirectStatus === 'requires_action' || redirectStatus === 'processing') {
      this.status = 'pending';
      this.message = 'Your payment is being processed. We will notify you by text once it is complete.';
    } else {
      this.status = 'error';
      this.message = 'Your payment could not be completed. Please try again or contact us for help.';
    }
  }

  private confirmBooking(intentId: string, intentType: 'setup_intent' | 'payment_intent', payable: number, taxCalcId: string): void {
    const booking = JSON.parse(localStorage.getItem('booking') || '{}');
    const body: any = {
      customer_id: this.authService.getCustomerId(),
      customer_name: this.authService.getCustomerName(),
      payable,
      tripname: localStorage.getItem('tripname') || '',
      favouritetrip: localStorage.getItem('favourite') || '',
      bookingdata: booking,
      tax_calc_id: taxCalcId || null
    };
    if (intentType === 'setup_intent') {
      body.setup_intent_id = intentId;
    } else {
      body.payment_intent_id = intentId;
    }

    this.http.post<any>(
      GlobalConstants.apiURL + 'confirm_booking',
      body,
      { headers: { 'Content-type': 'application/json' } }
    ).subscribe({
      next: res => {
        if (res.status === 'success') {
          this.status = 'success';
          this.message = 'Your booking is confirmed! Ride confirmation and driver details will be sent to your phone.';
        } else {
          this.status = 'error';
          this.message = res.message || 'Booking confirmation failed. Please contact support.';
        }
      },
      error: () => {
        this.status = 'error';
        this.message = 'Could not confirm booking. Please contact support with your payment reference.';
      }
    });
  }

  private confirmPayment(paymentIntentId: string, tripId: string, taxCalcId: string): void {
    this.http.post<any>(
      GlobalConstants.apiURL + 'confirm_payment',
      { trip_id: tripId, payment_intent_id: paymentIntentId, tax_calc_id: taxCalcId || null },
      { headers: { 'Content-type': 'application/json' } }
    ).subscribe({
      next: res => {
        if (res.success) {
          this.status = 'success';
          this.message = `Your payment of $${parseFloat(res.amount || '0').toFixed(2)} has been received.`;
        } else {
          this.status = 'error';
          this.message = res.message || 'Payment confirmation failed. Please contact support.';
        }
      },
      error: () => {
        this.status = 'error';
        this.message = 'Could not confirm payment. Please contact support with your payment reference.';
      }
    });
  }

  goToBookings(): void {
    this.router.navigate(['/my-bookings']).then(() => window.location.reload());
  }
}
